/**
 * Web push state: permission + subscription lifecycle.
 *
 * The load-bearing piece is syncPush(): a subscription can vanish under us
 * (browser eviction, a reinstall, and — until BUG-057 — every app update,
 * which used to unregister the worker), so every boot re-subscribes and
 * re-POSTs (the backend upsert is idempotent). A SW-side pushsubscriptionchange
 * handler is
 * deliberately NOT used: the SW can't authenticate the POST (JWT lives in
 * localStorage) and iOS support for that event is unreliable.
 */
import { create } from 'zustand';
import { api } from '../api/client';

const ENABLED_KEY = 'momento-push-enabled';
const NUDGE_DISMISSED_KEY = 'momento-push-nudge-dismissed';

const supported =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

const isIOS = typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent);

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true);

function urlBase64ToUint8Array(base64: string): BufferSource {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes.buffer;
}

async function subscribeAndRegister(): Promise<boolean> {
  const keyResp = await api.get<{ key: string }>('/push/public-key');
  if (!keyResp.success || !keyResp.data?.key) return false;

  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResp.data.key),
    }));

  const json = sub.toJSON();
  if (!json.endpoint || !json.keys) return false;
  const resp = await api.post('/push/subscriptions', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return resp.success;
}

interface PushState {
  supported: boolean;
  /** iOS browsers only get push once the PWA is installed to the home screen */
  needsInstall: boolean;
  enabled: boolean;
  busy: boolean;
  nudgeDismissed: boolean;
  enablePush: () => Promise<boolean>;
  disablePush: () => Promise<void>;
  syncPush: () => Promise<void>;
  dismissNudge: () => void;
}

export const usePushStore = create<PushState>((set, get) => ({
  supported,
  needsInstall: isIOS && !isStandalone(),
  enabled: supported && localStorage.getItem(ENABLED_KEY) === '1',
  busy: false,
  nudgeDismissed: localStorage.getItem(NUDGE_DISMISSED_KEY) === '1',

  // Must run inside a user gesture (iOS requirement for the permission prompt)
  enablePush: async () => {
    if (!get().supported || get().needsInstall) return false;
    set({ busy: true });
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;
      const ok = await subscribeAndRegister();
      if (ok) {
        localStorage.setItem(ENABLED_KEY, '1');
        set({ enabled: true });
      }
      return ok;
    } catch (e) {
      console.warn('[push] enable failed', e);
      return false;
    } finally {
      set({ busy: false });
    }
  },

  disablePush: async () => {
    set({ busy: true });
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete(`/push/subscriptions?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
    } catch (e) {
      console.warn('[push] disable failed', e);
    } finally {
      localStorage.removeItem(ENABLED_KEY);
      set({ enabled: false, busy: false });
    }
  },

  // Boot-time silent resubscribe (no gesture needed once permission is granted)
  syncPush: async () => {
    if (!get().supported || !get().enabled) return;
    if (Notification.permission !== 'granted') {
      localStorage.removeItem(ENABLED_KEY);
      set({ enabled: false });
      return;
    }
    try {
      await subscribeAndRegister();
    } catch (e) {
      console.warn('[push] sync failed', e);
    }
  },

  dismissNudge: () => {
    localStorage.setItem(NUDGE_DISMISSED_KEY, '1');
    set({ nudgeDismissed: true });
  },
}));
